/* eslint-disable camelcase */
import React, { useMemo, useState } from "react";
import { ReactComponent as InformationIcon } from "./../../images/svg/information.svg";
import { ReactComponent as LoadingIcon } from "./../../images/svg/loader.svg";
import { ReactComponent as ExportIcon } from "./../../images/svg/export.svg";
import { ReactComponent as ArrowDownIcon } from "./../../images/svg/arrowDown.svg";
import { gettext, pgettext, cropr } from "../../adapter";
import moment from "moment";
import WindChart from "./WindChart";
import RhRadiationChart from "./RhRadiationChart";
import TemperatureChart from "./TemperatureChart";
import PrecipitationChart from "./PrecipitationChart";
import WeatherDataBrush from "./WeatherDataBrush";
import { useSelector, useDispatch } from "react-redux";
import WeatherDataAverages from "./WeatherDataAverages";
import DateRangeField from "../shared/DateRangeField";
import DataGrid from "../shared/DataGrid";
import {
  calcHeat,
  calcMax,
  calcMean,
  calcMin,
  calcRich,
  calcWinddir,
  export2Csv,
  exportDay2Csv,
  parseStation,
  applyDateRange,
  applyFullDateRange,
} from "../../utils/weatherStationUtils";

const WeatherDataCharts = ({ apiUrl }) => {
  const [helpOpen, setHelpOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportDayOpen, setExportDayOpen] = useState(false);

  const dispatch = useDispatch();

  const stationState = useSelector((state) => {
    return state.weatherStation;
  });

  const { station, isFetching, domain, defaultDomain } = useMemo(() => {
    if (!stationState.station) {
      return { ...stationState };
    } else {
      return {
        ...stationState,
        ...parseStation(stationState.station),
      };
    }
  }, [stationState]);

  const currentDomain = domain ? domain : defaultDomain;
  const onDomainChange = station ? applyDateRange(station, dispatch) : () => {};
  const onFullDomainChange = station ? applyFullDateRange(apiUrl, station, dispatch) : () => {};

  var hcolumns = [
    { name: "date", title: gettext("Date"), width: 100, style: { fontWeight: "italic" } },
    { name: "time", title: gettext("Time"), width: 70 },
  ];

  var dcolumns = [{ name: "date", title: gettext("Date"), width: 100 }];

  if (station) {
    var meta = station.meta;
    if (meta.includes("air_temperature")) {
      hcolumns.push({
        name: "at",
        title: pgettext("temperature abrreviation", "T") + " " + cropr.l10n.unit_temp_display,
        type: "numericColumn",
      });
      dcolumns.push({
        name: "atmean",
        title:
          pgettext("mean temperature abrreviation", "Tmean") + " " + cropr.l10n.unit_temp_display,
        type: "numericColumn",
      });
      dcolumns.push({
        name: "atmin",
        title:
          pgettext("minimal temperature abrreviation", "Tmin") + " " + cropr.l10n.unit_temp_display,
        type: "numericColumn",
      });
      dcolumns.push({
        name: "atmax",
        title:
          pgettext("maximum temperature abrreviation", "Tmax") + " " + cropr.l10n.unit_temp_display,
        type: "numericColumn",
      });
    }
    if (meta.includes("rel_humidity")) {
      hcolumns.push({
        name: "rh",
        title: pgettext("relative humidity abrreviation", "RH") + " %",
        type: "numericColumn",
      });
      dcolumns.push({
        name: "rhmin",
        title: pgettext("mimimum relative humidity abrreviation", "RHmin") + " %",
        type: "numericColumn",
      });
    }
    if (meta.includes("radiation")) {
      hcolumns.push({
        name: "rad",
        title: pgettext("radiation abrreviation", "Rad") + " J/cm\u00b2",
        type: "numericColumn",
      });
      dcolumns.push({
        name: "rad",
        title: pgettext("radiation abrreviation", "Rad") + " J/cm\u00b2",
        type: "numericColumn",
      });
    }
    if (meta.includes("precipitation")) {
      hcolumns.push({
        name: "prec",
        title:
          pgettext("Precipitation abbreviation", "Precip.") +
          " " +
          cropr.l10n.unit_precipitation_display +
          pgettext("per hour abbreviation", "/h"),
        type: "numericColumn",
      });
      dcolumns.push({
        name: "prec",
        title:
          pgettext("Precipitation abbreviation", "Precip.") +
          " " +
          cropr.l10n.unit_precipitation_display +
          "/d",
        type: "numericColumn",
      });
    }
    if (meta.includes("wind_speed") || meta.includes("wind_direction")) {
      hcolumns.push({
        name: "ws",
        title: gettext("Wind") + " " + cropr.l10n.unit_windspeed_display,
        type: "numericColumn",
      });
      hcolumns.push({
        name: "wd",
        title: gettext("Wind") + " \u00b0",
        type: "rightAligned",
      });
      dcolumns.push({
        name: "ws",
        title: gettext("Wind ") + cropr.l10n.unit_windspeed_display,
        type: "numericColumn",
      });
      dcolumns.push({
        name: "wd",
        title: gettext("Wind") + " \u00b0",
        type: "numericColumn",
      });
    }
    if (
      meta.includes("wind_speed") &&
      meta.includes("air_temperature") &&
      meta.includes("radiation") &&
      meta.includes("rel_humidity")
    ) {
      dcolumns.push({
        name: "et",
        title: gettext("ETo") + " " + cropr.l10n.unit_precipitation_display + "/d",
        textAlign: "numericColumn",
      });
    }
  }

  var ccolumns = [
    { name: "date", title: gettext("Date") },
    {
      name: "atmean",
      title:
        pgettext("mean temperature abrreviation", "Tmean") +
        " (" +
        cropr.l10n.unit_temp_display +
        ")",
      type: "numericColumn",
    },
    {
      name: "rich",
      title: "Richardson (" + cropr.l10n.unit_temp_display + ")",
      type: "numericColumn",
    },
    {
      name: "inf",
      title: "Infruitec (" + cropr.l10n.unit_temp_display + ")",
      type: "numericColumn",
    },
    { name: "pn", title: "Pienaar North", type: "numericColumn" },
    { name: "pw", title: "Pienaar West", type: "numericColumn" },
    { name: "heat", title: "Heat (" + cropr.l10n.unit_temp_display + ")", type: "numericColumn" },
  ];

  var dataSethour = [];
  var dataSetday = [];
  var dataSetchill = [];

  var exportClass = "panel-options__item dropdown__wrapper";
  if (exportOpen) {
    exportClass = "panel-options__item dropdown__wrapper is-open";
  }

  var exportDClass = "panel-options__item dropdown__wrapper";
  if (exportDayOpen) {
    exportDClass = "panel-options__item dropdown__wrapper is-open";
  }

  var preloaderClass = "object-preloader is-loaded";
  if (isFetching) {
    preloaderClass = "object-preloader is-loading";
  }

  if (!station) return <div />;

  if (!station.measurements.raw || station.measurements.raw.length === 0) {
    return (
      <div>
        <div>
          <div className="l-full">
            <div className="panel has-object-preloader">
              <div className={preloaderClass}>
                <div className="object-preloader--content">
                  <LoadingIcon className="loader" />
                </div>
              </div>
              <h2>{station.name}</h2>
              <p>{gettext("This station lacks sufficient measurements for displaying a graph.")}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  var precipitation_precision = cropr.l10n.unit_precipitation === "in" ? 2 : 1;

  var meas = station.measurements.raw;
  dataSethour = [];
  dataSetday = [];
  dataSetchill = [];
  var dates = [];
  var d = new Date();
  var offset = d.getTimezoneOffset() * 1000 * 60;
  var pienn = "";
  var pienw = "";
  var zoekindex;
  // Hourly data
  for (var i = meas.length - 1; i >= 0; i--) {
    let d = new Date(meas[i].timestamp * 1000 + offset);
    if (d >= currentDomain[0] && d <= currentDomain[1]) {
      dataSethour.push({
        id: meas[i].timestamp,
        date: moment(d).format("L"),
        time: moment(d).format("LT"),
        at: cropr.toLocaleFixed(
          cropr.l10n_utils.celsius_to_l10n(meas[i].values.air_temperature),
          1
        ),
        rh: Math.round(meas[i].values.rel_humidity),
        rad: Math.round(meas[i].values.radiation),
        prec: cropr.toLocaleFixed(
          cropr.l10n_utils.mm_to_l10n(meas[i].values.precipitation),
          precipitation_precision
        ),
        ws: cropr.toLocaleFixed(cropr.l10n_utils.mps_to_l10n(meas[i].values.wind_speed), 1),
        wd: calcWinddir(meas[i].values.wind_direction),
      });
      // group for daily data chill factors
      zoekindex = dates.findIndex((obj) => {
        return obj.date === moment(d).format("L");
      });
      if (zoekindex < 0) {
        dates.push({ date: moment(d).format("L"), d: d, at: [] });
        zoekindex = dates.length - 1;
      }
      dates[zoekindex].at.push(cropr.l10n_utils.celsius_to_l10n(meas[i].values.air_temperature));
    }
  }
  for (i = 0; i < station.day_measurements.length; i++) {
    d = new Date(station.day_measurements[i].timestamp * 1000 + offset);
    var values = station.day_measurements[i].values;
    if (d >= currentDomain[0] && d <= currentDomain[1]) {
      dataSetday.push({
        id: i,
        date: moment(d).format("L"),
        atmean: cropr.toLocaleFixed(
          cropr.l10n_utils.celsius_to_l10n(values.air_temperature_mean),
          1
        ),
        atmin: cropr.toLocaleFixed(cropr.l10n_utils.celsius_to_l10n(values.air_temperature_min), 1),
        atmax: cropr.toLocaleFixed(cropr.l10n_utils.celsius_to_l10n(values.air_temperature_max), 1),
        rhmin: Math.round(values.rel_humidity_min),
        rad: Math.round(values.radiation_sum),
        prec: cropr.toLocaleFixed(
          cropr.l10n_utils.mm_to_l10n(values.precipitation_sum),
          precipitation_precision
        ),
        ws: cropr.toLocaleFixed(cropr.l10n_utils.mps_to_l10n(values.wind_speed_mean), 1),
        wd: calcWinddir(values.wind_direction_mean),
        et: cropr.toLocaleFixed(cropr.l10n_utils.mm_to_l10n(values.et_nul), 1),
      });
    }
  }
  dataSetday.reverse();

  // chill stuff
  for (i = 0; i < dates.length; i++) {
    var obj = dates[i];

    pienn = "";
    pienw = "";
    if (obj.d.getMonth() === 4 && obj.d.getDate() <= 21) {
      if (calcMin(obj.at) < 10.0 && calcMax(obj.at) <= 20.0) {
        pienn = "1000";
      } else {
        pienn = "0";
      }
    }
    if (
      (obj.d.getMonth() === 4 && obj.d.getDate() >= 17) ||
      (obj.d.getMonth() === 5 && obj.d.getDate() <= 7)
    ) {
      if (calcMin(obj.at) < 10.0 && calcMax(obj.at) <= 20.0) {
        pienw = "1000";
      } else {
        pienw = "0";
      }
    }
    dataSetchill.push({
      id: i,
      date: obj.date,
      atmean: cropr.toLocaleFixed(cropr.l10n_utils.celsius_to_l10n(calcMean(obj.at)), 1),
      richday: cropr.l10n_utils.celsius_to_l10n(calcRich(obj.at)),
      pn: pienn,
      pw: pienw,
      heatday: cropr.l10n_utils.celsius_to_l10n(calcHeat(obj.at)),
    });
  }

  // cumuleren en weer omdraaien
  var richcum = 0;
  var infrcum = 0;
  var heatcum = 0;
  for (i = dataSetchill.length - 1; i >= 0; i--) {
    richcum += dataSetchill[i].richday;
    dataSetchill[i].rich = cropr.l10n_utils.celsius_to_l10n(richcum);
    heatcum += dataSetchill[i].heatday;
    dataSetchill[i].heat = cropr.toLocaleFixed(cropr.l10n_utils.celsius_to_l10n(heatcum), 1);
    if (dataSetchill[i].richday >= 0) {
      infrcum += dataSetchill[i].richday;
    }
    dataSetchill[i].inf = cropr.l10n_utils.celsius_to_l10n(infrcum);
  }
  dataSetchill.reverse();

  var chilldiv = "";
  if (station.chill === true) {
    var chillHelpLink = "https://www.dacom.nl/dacom_online_help/chill.html";
    var helpClass = "panel-options__item dropdown__wrapper";
    if (helpOpen) {
      helpClass = "panel-options__item dropdown__wrapper is-open";
    }

    chilldiv = (
      <div className="l-full">
        <div className="panel has-object-preloader">
          <div className="panel--header">
            <h2>Chill units</h2>
            <ul className="panel-options">
              <li
                className={helpClass}
                onClick={() => {
                  setHelpOpen(!helpOpen);
                }}
              >
                {/* eslint-disable-next-line */}
                <a className="dropdown--toggle">
                  <InformationIcon className="icon" />
                </a>

                <div className="dropdown dropdown--wide">
                  <div className="dropdown__content">
                    <iframe title="Help link" src={chillHelpLink}></iframe>
                  </div>
                </div>
              </li>
            </ul>
          </div>
          <DataGrid
            rows={dataSetchill}
            columns={ccolumns.map((c) => ({ key: c.name, title: c.title, type: c.type }))}
            maxHeight={400}
          />
          <br />
        </div>
      </div>
    );
  }

  var winddiv = "";
  if (station) {
    if (
      "wind_direction" in station.measurements.raw[0].values ||
      "wind_speed" in station.measurements.raw[0].values
    ) {
      winddiv = <WindChart station={station} domain={currentDomain} />;
    }
  }
  var rhdiv = "";
  if (station) {
    if (
      "rel_humidity" in station.measurements.raw[0].values ||
      "radiation" in station.measurements.raw[0].values
    ) {
      rhdiv = <RhRadiationChart station={station} domain={currentDomain} />;
    }
  }
  var temperaturediv = "";
  if (station) {
    if ("air_temperature" in station.measurements.raw[0].values) {
      temperaturediv = <TemperatureChart station={station} domain={currentDomain} />;
    }
  }
  var precipitationdiv = "";
  if (station) {
    if ("precipitation" in station.measurements.raw[0].values) {
      precipitationdiv = <PrecipitationChart station={station} domain={currentDomain} />;
    }
  }

  return (
    <div>
      <div>
        <div className="l-full">
          <div className="panel has-object-preloader">
            <div className={preloaderClass}>
              <div className="object-preloader--content">
                <LoadingIcon className="loader" />
              </div>
            </div>
            <h2>
              {station.name} {gettext("date range filter")}
              <DateRangeField
                startDate={station.firstDataPointDate}
                endDate={station.lastDataPointDate}
                minDate={station.startDate}
                maxDate={new Date()}
                onApply={onFullDomainChange}
              />
            </h2>
            <WeatherDataBrush station={station} domain={currentDomain} />
          </div>
        </div>

        <div className="l-full">
          <div className="panel has-object-preloader">
            <div className={preloaderClass}>
              <div className="object-preloader--content">
                <LoadingIcon className="loader" />
              </div>
            </div>
            <div className="panel--header">
              <h2>
                {gettext("Weather data development")}{" "}
                <DateRangeField
                  startDate={currentDomain ? currentDomain[0] : null}
                  endDate={currentDomain ? currentDomain[1] : null}
                  minDate={station.firstDataPointDate}
                  maxDate={station.lastDataPointDate}
                  onApply={onDomainChange}
                />
              </h2>

              <ul className="panel-options">
                <li
                  className={exportClass}
                  onClick={() => {
                    setExportOpen(!exportOpen);
                  }}
                >
                  {/* eslint-disable-next-line */}
                  <a className="dropdown--toggle">
                    <ExportIcon className="icon" /> {gettext("Export")}
                  </a>

                  <div className="dropdown">
                    <ul className="dropdown__list">
                      <li>
                        {/* eslint-disable-next-line */}
                        <a onClick={() => export2Csv(station)}>{gettext("To CSV")}</a>
                      </li>
                    </ul>
                  </div>
                </li>
              </ul>

              <div className="panel--settings">
                <div className="dropdown">
                  {/* eslint-disable-next-line */}
                  <a className="dropdown--toggle">
                    <ArrowDownIcon className="icon dropdown--arrow" />
                    {gettext("Export")}
                  </a>
                  <div className="dropdown--popout ">
                    <ul className="dropdown--list">
                      <li>
                        {/* eslint-disable-next-line */}
                        <a onClick={() => export2Csv(station)}>{gettext("To CSV")}</a>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="panel--settings__item">
                  <div
                    id="information"
                    className="dropdown"
                    onClick={() => {
                      var info = document.querySelector("#information"); // Using a class instead, see note below.
                      info.classList.toggle("is-open");
                    }}
                  >
                    {/* eslint-disable-next-line */}
                    <a className="panel--settings__icon">
                      <InformationIcon className="icon" />
                    </a>
                    <div className="dropdown--popout is-visible dropdown--popout__moretop dropdown--popout__wide">
                      <div className="dropdown--popout__content">
                        <p>Ik ben de HTML content</p>
                        <h3>test</h3>
                        <p>Ik ben de HTML content</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/*

              Dit hadden we al om een map te maken. Hij moet alleen lager in de html staan. Wanneer ik googlemaps include gaat het kapot :)
            <div className="modal is-visible">
              <div className="modal--content">
              </div>
            </div>
            */}
            {temperaturediv}
            {rhdiv}
            {precipitationdiv}
            {winddiv}
          </div>
        </div>
      </div>

      <div className="l-full">
        <div className="panel has-object-preloader">
          <div className={preloaderClass}>
            <div className="object-preloader--content">
              <LoadingIcon className="loader" />
            </div>
          </div>

          <div className="panel--header">
            <h2>
              {gettext("Totals")}{" "}
              <DateRangeField
                startDate={currentDomain ? currentDomain[0] : null}
                endDate={currentDomain ? currentDomain[1] : null}
                minDate={station.firstDataPointDate}
                maxDate={station.lastDataPointDate}
                onApply={onDomainChange}
              />
            </h2>
          </div>
          <WeatherDataAverages station={station} domain={currentDomain} />
        </div>
      </div>

      <div className="l-full">
        <div className="panel has-object-preloader">
          <div className={preloaderClass}>
            <div className="object-preloader--content">
              <LoadingIcon className="loader" />
            </div>
          </div>

          <div className="panel--header">
            <h2>
              {gettext("Totals")}{" "}
              <DateRangeField
                startDate={currentDomain ? currentDomain[0] : null}
                endDate={currentDomain ? currentDomain[1] : null}
                minDate={station.firstDataPointDate}
                maxDate={station.lastDataPointDate}
                onApply={onDomainChange}
              />
            </h2>

            <ul className="panel-options">
              <li
                className={exportClass}
                onClick={() => {
                  setExportOpen(!exportOpen);
                }}
              >
                {/* eslint-disable-next-line */}
                <a className="dropdown--toggle">
                  <ExportIcon className="icon" /> {gettext("Export")}
                </a>

                <div className="dropdown">
                  <ul className="dropdown__list">
                    <li>
                      {/* eslint-disable-next-line */}
                      <a onClick={() => export2Csv(station)}>{gettext("To CSV")}</a>
                    </li>
                  </ul>
                </div>
              </li>
            </ul>

            <div className="panel--settings">
              <div className="dropdown">
                {/* eslint-disable-next-line */}
                <a className="dropdown--toggle">
                  <ArrowDownIcon className="icon dropdown--arrow" />
                  {gettext("Export")}
                </a>
                <div className="dropdown--popout ">
                  <ul className="dropdown--list">
                    <li>
                      {/* eslint-disable-next-line */}
                      <a onClick={() => export2Csv(station)}>{gettext("To CSV")}</a>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="panel--settings__item">
                <div
                  id="information"
                  className="dropdown"
                  onClick={() => {
                    var info = document.querySelector("#information"); // Using a class instead, see note below.
                    info.classList.toggle("is-open");
                  }}
                >
                  {/* eslint-disable-next-line */}
                  <a className="panel--settings__icon">
                    <InformationIcon className="icon" />
                  </a>
                  <div className="dropdown--popout is-visible dropdown--popout__moretop dropdown--popout__wide">
                    <div className="dropdown--popout__content">
                      <p>Ik ben de HTML content</p>
                      <h3>test</h3>
                      <p>Ik ben de HTML content</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DataGrid
            rows={dataSethour}
            columns={hcolumns.map((c) => ({ key: c.name, title: c.title, type: c.type }))}
            maxHeight={400}
          />
          <br />
          <div className="panel--header">
            <ul className="panel-options">
              <li
                className={exportDClass}
                onClick={() => {
                  setExportDayOpen(!exportDayOpen);
                }}
              >
                {/* eslint-disable-next-line */}
                <a className="dropdown--toggle">
                  <ExportIcon className="icon" /> {gettext("Export")}
                </a>

                <div className="dropdown">
                  <ul className="dropdown__list">
                    <li>
                      {/* eslint-disable-next-line */}
                      <a onClick={() => exportDay2Csv(dcolumns, dataSetday)}>{gettext("To CSV")}</a>
                    </li>
                  </ul>
                </div>
              </li>
            </ul>

            <div className="panel--settings">
              <div className="dropdown">
                {/* eslint-disable-next-line */}
                <a className="dropdown--toggle">
                  <ArrowDownIcon className="icon dropdown--arrow" />
                  {gettext("Export")}
                </a>
                <div className="dropdown--popout ">
                  <ul className="dropdown--list">
                    <li>
                      {/* eslint-disable-next-line */}
                      <a onClick={() => exportDay2Csv(dcolumns, dataSetday)}>{gettext("To CSV")}</a>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="panel--settings__item">
                <div
                  id="information"
                  className="dropdown"
                  onClick={() => {
                    var info = document.querySelector("#information"); // Using a class instead, see note below.
                    info.classList.toggle("is-open");
                  }}
                >
                  {/* eslint-disable-next-line */}
                  <a className="panel--settings__icon">
                    <InformationIcon className="icon" />
                  </a>
                  <div className="dropdown--popout is-visible dropdown--popout__moretop dropdown--popout__wide">
                    <div className="dropdown--popout__content">
                      <p>Ik ben de HTML content</p>
                      <h3>test</h3>
                      <p>Ik ben de HTML content</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DataGrid
            rows={dataSetday}
            columns={dcolumns.map((c) => ({ key: c.name, title: c.title, type: c.type }))}
            maxHeight={400}
          />
        </div>
      </div>
      {chilldiv}
    </div>
  );
};

export default WeatherDataCharts;
